// src/components/SelectListMulti.tsx
import Select, { MultiValue } from "react-select";
import { withValidate } from "./withValidate";

interface SelectListMultiOption {
  value: string;
  label: string;
}

interface SelectListMultiProps {
  onChange: (value: string[]) => void;
  options: SelectListMultiOption[];
  placeholder?: string;
  isSearchable?: boolean;
  style?: React.CSSProperties;
  isDefaultValue?: boolean;
  defaultValues?: SelectListMultiOption[];
  value?: string[];
  boxHeight?: number;
  editable?: boolean;
  label?: string;
  required?: boolean;
  errorMessage?: string;
}

export default function SelectListMulti({
  onChange,
  options,
  placeholder = "선택하세요 (다중선택 가능)",
  isSearchable = true,
  style,
  isDefaultValue = false,
  defaultValues,
  value = [],
  boxHeight = 40,
  editable = true,
  label,
  required = false,
  errorMessage,
}: SelectListMultiProps) {
  // value 배열을 SelectListMultiOption 배열로 변환
  const selectedOptions = value
    .map((val) => options.find((option) => option.value === val))
    .filter(Boolean) as SelectListMultiOption[];

  return (
    <div className="w-full">
      {label && (
        <label className="block mb-1 text-sm font-medium text-gray-700">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <Select
        isMulti
        onChange={(selectedOptions: MultiValue<SelectListMultiOption>) => {
          const values = selectedOptions
            ? selectedOptions.map((option) => option.value)
            : [];
          onChange(values);
        }}
        options={options}
        placeholder={placeholder}
        classNamePrefix="react-select"
        menuPortalTarget={typeof window !== "undefined" ? document.body : null}
        value={selectedOptions}
        defaultValue={isDefaultValue ? defaultValues || [] : []}
        isSearchable={isSearchable}
        isDisabled={!editable}
        styles={{
          // body로 포탈되는 메뉴가 sticky 헤더/nav 등에 가려지지 않도록 z-index 명시
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, zIndex: 9999 }),
          control: (base, state) => ({
            ...base,
            minHeight: `${boxHeight}px`,
            borderRadius: "0.5rem",
            fontSize: "0.95rem",
            borderColor: state.isFocused ? "#cccccc" : "#cccccc",
            boxShadow: state.isFocused ? "0 0 0 2px #FF9934" : "none",
            "&:hover": {
              border: "1px solid #cccccc",
            },
            "&:focus": {
              border: "1px solid #cccccc",
            },
            ...style,
          }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
              ? "#FFC286" // 선택된 옵션
              : state.isFocused
                ? "#FF9934 " // hover
                : "white",
            color: state.isSelected || state.isFocused ? "white" : "black",
            cursor: "pointer",
            // 아래 추가: active 상태도 커스텀
            ":active": {
              backgroundColor: "#FF9934", // 클릭 시에도 주황색
              color: "white",
            },
          }),
          multiValue: (provided) => ({
            ...provided,
            backgroundColor: "#FFC286",
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            color: "white",
          }),
          multiValueRemove: (provided) => ({
            ...provided,
            color: "white",
            ":hover": {
              backgroundColor: "#FF9934",
              color: "white",
            },
          }),
        }}
      />
      {errorMessage && (
        <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
      )}
    </div>
  );
}

export const ValidateSelectListMulti = withValidate(SelectListMulti);
